import { API_CONFIG, apiUtils } from '../config.js';

// Student Management API
export const studentApi = {
  // Register student to unit
  registerStudent: async (studentData) => {
    const url = `${API_CONFIG.BASE_URL}/unit-kind/register-student`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify(studentData),
    });
  },

  // Bulk upload students
  bulkUploadStudents: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    // We use raw fetch here to let browser set Content-Type with boundary for FormData
    const token = localStorage.getItem('authToken');
    const headers = {
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    const response = await fetch(`${API_CONFIG.BASE_URL}/student/bulk-upload`, {
      method: 'POST',
      headers,
      body: formData
    });

    return apiUtils.handleResponse(response);
  },

  // Get bulk upload sample
  getBulkUploadSample: async () => {
    const token = localStorage.getItem('authToken');
    const headers = {
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    const response = await fetch(`${API_CONFIG.BASE_URL}/student/bulk-upload-sample`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
       await apiUtils.handleResponse(response); // Will throw error
    }
    
    // Get the blob and explicitly set XLSX MIME type
    const arrayBuffer = await response.arrayBuffer();
    return new Blob([arrayBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
  },

  // Get all students (Paginated)
  getAllStudents: async (params = {}) => {
    const queryParams = apiUtils.buildQueryString({
      page: params.page || 1,
      limit: params.limit || 40,
      search: params.search,
      unitId: params.unitId,
      sectionId: params.sectionId,
      academicYear: params.academicYear,
      status: params.status
    });

    const url = `${API_CONFIG.BASE_URL}/student?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Get all students of a unit (Legacy support via new API)
  getStudentsByUnit: async (unitId, academicYear, sectionId) => {
    // Fetch with a high limit to simulate "get all for unit" behavior without pagination UI
    let url = `${API_CONFIG.BASE_URL}/student?unitId=${unitId}&limit=500`;
    if (academicYear) {
        url += `&academicYear=${academicYear}`;
    }
    if (sectionId) {
        url += `&sectionId=${sectionId}`;
    }
    
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Get student by ID
  getStudentById: async (id, academicYear) => {
    const params = academicYear ? `?academicYear=${academicYear}` : '';
    const url = `${API_CONFIG.BASE_URL}/student/${id}${params}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Update student by ID
  updateStudentById: async (id, studentData) => {
    const url = `${API_CONFIG.BASE_URL}/student/${id}`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'PUT',
      body: JSON.stringify(studentData),
    });
  },

  // Update student by registration ID
  updateStudent: async (registrationId, studentData) => {
    const url = `${API_CONFIG.BASE_URL}/student/update/${registrationId}`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'PUT',
      body: JSON.stringify(studentData),
    });
  },

  // Delete student
  deleteStudent: async (id) => {
    try {
      const token = localStorage.getItem('authToken');
      const headers = {
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}/student/delete/${id}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      return text ? JSON.parse(text) : { success: true };
    } catch (error) {
      apiUtils.handleError(error);
      throw error;
    }
  },

  // Bulk delete students
  bulkDeleteStudents: async (studentIds) => {
    const url = `${API_CONFIG.BASE_URL}/student/bulk-delete`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'PUT',
      body: JSON.stringify({ studentIds }),
    });
  },

  // Get student attendance
  getStudentAttendance: async (registrationId, fromDate, toDate) => {
    const queryParams = apiUtils.buildQueryString({ from: fromDate, to: toDate });
    const url = `${API_CONFIG.BASE_URL}/attendance/student/${registrationId}?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Get student results
  getStudentResults: async (registrationId, academicYear) => {
    const queryParams = apiUtils.buildQueryString({ academicYear });
    const url = `${API_CONFIG.BASE_URL}/results/student/${registrationId}?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Get student summary
  getStudentSummary: async (registrationId, academicYear) => {
    const queryParams = apiUtils.buildQueryString({ academicYear });
    const url = `${API_CONFIG.BASE_URL}/results/student/${registrationId}/summary?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Get student fees
  getStudentFees: async (registrationId, fromDate, toDate) => {
    const queryParams = apiUtils.buildQueryString({ from: fromDate, to: toDate });
    const url = `${API_CONFIG.BASE_URL}/fee/student/${registrationId}?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Get student statistics
  getStudentStats: async (academicYear) => {
    const queryParams = apiUtils.buildQueryString({ academicYear });
    const url = `${API_CONFIG.BASE_URL}/student/stats?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Get unassigned students by class
  getUnassignedStudentsByClass: async (unitId, academicYear) => {
    const queryParams = apiUtils.buildQueryString({ academicYear });
    const url = `${API_CONFIG.BASE_URL}/student/class/${unitId}/unassigned?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },
};

export default studentApi;
